import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../services/api';

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [emailSent, setEmailSent] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setMessage('');

        try {
            const response = await authAPI.forgotPassword(email);
            
            if (response.emailExists) {
                setEmailSent(true);
                setMessage('Password reset instructions have been sent to your email address.');
            }
        } catch (err) {
            if (err.response?.data?.emailExists === false) {
                setError('No account found with this email address.');
            } else {
                setError(err.response?.data?.message || 'Something went wrong. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    if (emailSent) {
        return (
            <div className="min-h-screen flex flex-wrap justify-center overflow-x-hidden">
                {/* Left Side: Blue BG with White Success Card */}
                <div className="w-full lg:w-1/2 min-w-[1024p] flex-none bg-blue-600 flex justify-end py-8">
                    <div className="max-w-xl w-full bg-white shadow-xl p-8 flex flex-col justify-center min-h-[600px]">
                        <div className="px-4 sm:px-6 lg:px-8 text-center">
                            {/* Success Icon */}
                            <div className="flex justify-center mb-6">
                                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                            </div>

                            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Check your email</h2>
                            <p className="text-gray-600 text-sm mb-6">
                                We've sent password reset instructions to<br />
                                <span className="font-medium text-gray-900">{email}</span>
                            </p>

                            <div className="space-y-4">
                                <p className="text-sm text-gray-600">
                                    Didn't receive the email? Check your spam folder or
                                </p>
                                
                                <button
                                    onClick={() => {
                                        setEmailSent(false);
                                        setEmail('');
                                        setMessage('');
                                    }}
                                    className="text-blue-600 hover:text-blue-500 font-medium text-sm"
                                >
                                    Try again
                                </button>

                                <div className="w-full pt-4">
                                    <Link
                                        to="/login"
                                        className="w-full flex justify-center py-2.5 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out"
                                    >
                                        Back to Login
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Side: Info Card */}
                <div className="w-full lg:w-1/2 min-w-[1024px] flex-none bg-gray-200 flex items-left py-8">
                    <div className="bg-blue-600 text-white shadow-xl p-9 max-w-xl w-full min-h-[600px]">
                        <div className="flex flex-col justify-center h-full">
                            {/* Profile Image */}
                            <div className="w-36 h-36 rounded-full overflow-hidden border-4 border-white/30 shadow-lg mb-6 mx-auto">
                                <img src="/images/signup2.jfif" alt="Doctor" className="w-full h-full object-cover" />
                            </div>

                            {/* Heading */}
                            <h2 className="text-2xl font-bold text-center mb-3">Password Recovery</h2>
                            <p className="text-base text-white/90 text-center mb-8">We're here to help you get back to your care</p>

                            {/* Feature List */}
                            <div className="space-y-6 text-left w-full max-w-sm mx-auto">
                                {/* Feature 1 */}
                                <div className="flex items-start space-x-4">
                                    <div className="w-10 h-10 bg-blue-500 backdrop-blur-md rounded-md flex items-center justify-center">
                                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-base font-semibold">Secure Reset Process</p>
                                        <p className="text-sm text-white/80">Your account security is our priority</p>
                                    </div>
                                </div>

                                {/* Feature 2 */}
                                <div className="flex items-start space-x-4">
                                    <div className="w-10 h-10 bg-blue-500 backdrop-blur-md rounded-md flex items-center justify-center">
                                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-base font-semibold">Email Verification</p>
                                        <p className="text-sm text-white/80">Reset link sent to your registered email</p>
                                    </div>
                                </div>

                                {/* Feature 3 */}
                                <div className="flex items-start space-x-4">
                                    <div className="w-10 h-10 bg-blue-500 backdrop-blur-md rounded-md flex items-center justify-center">
                                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-base font-semibold">Quick Recovery</p>
                                        <p className="text-sm text-white/80">Get back to your care journey in minutes</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-wrap justify-center overflow-x-hidden">
            {/* Left Side: Blue BG with White Forgot Password Card */}
            <div className="w-full lg:w-1/2 min-w-[1024p] flex-none bg-blue-600 flex justify-end py-8">
                <form
                    onSubmit={handleSubmit}
                    className="max-w-xl w-full bg-white shadow-xl p-8 flex flex-col justify-center min-h-[600px]"
                >
                    <div className="px-4 sm:px-6 lg:px-8">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-2 text-center">Forgot your password?</h2>
                        <p className="text-gray-600 text-sm mb-6 text-center">Enter your email address and we'll send you a link to reset your password.</p>

                        {/* Success Message */}
                        {message && (
                            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                                <p className="text-sm text-green-600">{message}</p>
                            </div>
                        )}

                        {/* Error Display */}
                        {error && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                                <p className="text-sm text-red-600">{error}</p>
                            </div>
                        )}

                        {/* Email Input */}
                        <div className="mt-4">
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email address</label>
                            <input
                                type="email"
                                name="email"
                                id="email"
                                placeholder="john@email.com"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    if (error) setError(''); // Clear error when typing
                                }}
                                required
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-md placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                        </div>

                        <div className="w-full pt-6">
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Sending...
                                    </>
                                ) : (
                                    'Send reset link'
                                )}
                            </button>
                        </div>

                        <p className="text-center text-sm mt-4 text-gray-600">
                            Remember your password?{' '}
                            <Link to="/login" className="text-blue-600 hover:underline font-medium">Back to Login</Link>
                        </p>
                    </div>
                </form>
            </div>

            {/* Right Side: Info Card */}
            <div className="w-full lg:w-1/2 min-w-[1024px] flex-none bg-gray-200 flex items-left py-8">
                <div className="bg-blue-600 text-white shadow-xl p-9 max-w-xl w-full min-h-[600px]">
                    <div className="flex flex-col justify-center h-full">
                        {/* Profile Image */}
                        <div className="w-36 h-36 rounded-full overflow-hidden border-4 border-white/30 shadow-lg mb-6 mx-auto">
                            <img src="/images/signup2.jfif" alt="Doctor" className="w-full h-full object-cover" />
                        </div>

                        {/* Heading */}
                        <h2 className="text-2xl font-bold text-center mb-3">Password Recovery</h2>
                        <p className="text-base text-white/90 text-center mb-8">We're here to help you get back to your care</p>

                        {/* Feature List */}
                        <div className="space-y-6 text-left w-full max-w-sm mx-auto">
                            {/* Feature 1 */}
                            <div className="flex items-start space-x-4">
                                <div className="w-10 h-10 bg-blue-500 backdrop-blur-md rounded-md flex items-center justify-center">
                                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-base font-semibold">Secure Reset Process</p>
                                    <p className="text-sm text-white/80">Your account security is our priority</p>
                                </div>
                            </div>

                            {/* Feature 2 */}
                            <div className="flex items-start space-x-4">
                                <div className="w-10 h-10 bg-blue-500 backdrop-blur-md rounded-md flex items-center justify-center">
                                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-base font-semibold">Email Verification</p>
                                    <p className="text-sm text-white/80">Reset link sent to your registered email</p>
                                </div>
                            </div>

                            {/* Feature 3 */}
                            <div className="flex items-start space-x-4">
                                <div className="w-10 h-10 bg-blue-500 backdrop-blur-md rounded-md flex items-center justify-center">
                                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-base font-semibold">Quick Recovery</p>
                                    <p className="text-sm text-white/80">Get back to your care journey in minutes</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword; 